// The shell: a phone-shaped app. A full-screen vertical feed of games (each card runs its game
// live in the page with bots), a Popular list, Create (the generation pipeline, create.ts), and
// a Profile.
// Entering a game mounts the game view over everything; the × returns to where you were.
// Routes live in the hash so links are shareable:
//   #feed  #saved  #popular  #create  #create?remix=blobs  #profile  #game/blobs?room=main

import { mountGame, type GameHandle } from "./game.js";
import type { Meta } from "../contract.js";
import { mountPreview, type PreviewHandle } from "./preview.js";
import { mountCreate } from "./create.js";
import { startLocalServer, type LocalServer } from "./localserver.js";
import { games } from "../../games/index.js";
import { serverAddress, NO_SERVER_MESSAGE, createLocked } from "./config.js";
import { icons, icon, type IconName } from "./icons.js";

type Route =
  | { view: "feed"; saved: boolean }
  | { view: "popular" }
  | { view: "create"; remix?: string }
  | { view: "profile" }
  | { view: "game"; slug: string; room: string; draft?: boolean };

interface FeedGame {
  slug: string;
  name: string;
  blurb: string;
  players: number;
  rooms: number;
  max: number;
  plays: number;
  likes: number;
  saves: number;
  shares: number;
}

interface Card {
  el: HTMLElement;
  title: HTMLElement;
  live: HTMLElement;
  canvas: HTMLCanvasElement;
  preview: PreviewHandle | null;
  counts: Record<"like" | "share" | "save", HTMLElement>;
  buttons: Record<"like" | "save", HTMLButtonElement>;
}

interface Recent {
  slug: string;
  count: number;
  at: number;
}

const AUTHOR = "hazycausation";
const FEED_REFRESH_MS = 3000;

const el = (id: string) => document.getElementById(id) as HTMLElement;
const views: Record<Route["view"], HTMLElement> = {
  feed: el("view-feed"),
  popular: el("view-popular"),
  create: el("view-create"),
  profile: el("view-profile"),
  game: el("game"),
};
const tabs = Array.from(document.querySelectorAll<HTMLAnchorElement>("#bar a[data-tab]"));
const pills = Array.from(document.querySelectorAll<HTMLAnchorElement>("#top a[data-feed]"));
const feed = el("feed");
const feedEmpty = el("feed-empty");
const popularList = el("popular");
const nameInput = el("name") as HTMLInputElement;
const promptBox = el("prompt") as HTMLTextAreaElement;
const toastEl = el("toast");
const locked = createLocked();
if (locked) {
  // A small lock on the Create button; the tab itself says why.
  const a = document.querySelector<HTMLAnchorElement>("#bar a.create");
  if (a) {
    a.insertAdjacentHTML("beforeend", `<span class="lock">${icon("lock")}</span>`);
    a.setAttribute("aria-label", "Create (locked in this demo)");
    a.title = "Create: locked in this demo";
  }
}
const create = mountCreate({
  address: serverAddress(),
  locked,
  els: {
    prompt: promptBox,
    build: el("build") as HTMLButtonElement,
    status: el("gen-status"),
    log: el("gen-log"),
    result: el("gen-result"),
    preview: el("gen-preview") as HTMLCanvasElement,
    spec: el("gen-spec"),
    findings: el("gen-findings"),
    play: el("play") as HTMLButtonElement,
    add: el("add") as HTMLButtonElement,
    publish: el("publish") as HTMLButtonElement,
    download: el("download") as HTMLButtonElement,
    change: el("change") as HTMLButtonElement,
    outcome: el("gen-outcome"),
  },
  author: () => nameInput.value.trim(),
  toast: (msg) => toast(msg),
});

for (const node of document.querySelectorAll<HTMLElement>("[data-icon]")) {
  node.innerHTML = icon(node.dataset.icon as IconName);
}

let handle: GameHandle | null = null;
/** The in-page server a Create draft is being played on, stopped with the handle. */
let localServer: LocalServer | null = null;
/** What the handle was mounted for, so a share link to another game while playing switches games. */
let mounted: { slug: string; room: string } | null = null;
let mounting = false;
let feedTimer: number | undefined;
let latest: FeedGame[] = [];
let lastNonGame: string = "#feed";
const cards = new Map<string, Card>();

// ---- storage -------------------------------------------------------------------------------
// Per-viewer conveniences only. Sandboxed iframes throw on localStorage access, so try is the
// only guard; degrade, never throw.

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* no storage here */
  }
}

const liked = new Set<string>(readJSON<string[]>("p2m.liked", []));
const saved = new Set<string>(readJSON<string[]>("p2m.saved", []));
let recent = readJSON<Recent[]>("p2m.recent", []);

// ---- name and avatar -----------------------------------------------------------------------

nameInput.value = new URLSearchParams(location.search).get("name") ?? readJSON<string>("p2m.name", "");
nameInput.addEventListener("change", () => {
  writeJSON("p2m.name", nameInput.value.trim());
  renderProfile();
});

function avatarColour(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 70% 60%)`;
}

// ---- routing --------------------------------------------------------------------------------

function parseRoute(): Route {
  const hash = location.hash.replace(/^#/, "");
  const [path, query = ""] = hash.split("?");
  const params = new URLSearchParams(query);
  if (path.startsWith("game/")) {
    const slug = path.slice("game/".length).toLowerCase().replace(/[^a-z0-9-]/g, "");
    const room = (params.get("room") ?? "main").toLowerCase().replace(/[^a-z0-9-]/g, "") || "main";
    if (slug) return { view: "game", slug, room };
  }
  // The game the Create tab just built, played against a server inside this page.
  if (path === "draft") return { view: "game", slug: "draft", room: "main", draft: true };
  if (path === "saved") return { view: "feed", saved: true };
  if (path === "popular") return { view: "popular" };
  if (path === "create") return { view: "create", remix: params.get("remix") ?? undefined };
  if (path === "profile") return { view: "profile" };
  return { view: "feed", saved: false };
}

async function render(): Promise<void> {
  const route = parseRoute();

  const leaving = route.view !== "game" || (mounted !== null && (mounted.slug !== route.slug || mounted.room !== route.room));
  if (route.view !== "game") lastNonGame = location.hash || "#feed";
  if (leaving && handle) {
    handle.stop();
    handle = null;
    localServer?.close();
    localServer = null;
    mounted = null;
    hideControls(true);
  }
  for (const [view, node] of Object.entries(views)) node.hidden = view !== route.view;
  document.body.classList.toggle("in-game", route.view === "game");
  document.body.classList.toggle("on-feed", route.view === "feed");
  for (const a of tabs) a.classList.toggle("active", a.dataset.tab === route.view);
  for (const a of pills) a.classList.toggle("active", route.view === "feed" && a.dataset.feed === (route.saved ? "saved" : "foryou"));
  (el("exit") as HTMLAnchorElement).href = lastNonGame;

  if (route.view === "feed") {
    startFeed();
    applyFilter(route.saved);
  } else stopFeed();

  if (route.view === "popular") void refreshPopular();
  if (route.view === "profile") renderProfile();
  create.setActive(route.view === "create");
  if (route.view === "create" && route.remix) {
    const g = latest.find((x) => x.slug === route.remix);
    promptBox.value = `Remix of ${g ? g.name : route.remix}: `;
    promptBox.focus();
  }

  if (route.view === "game" && !handle && !mounting) {
    const draft = route.draft ? create.draft() : null;
    if (route.draft && !draft) {
      // Nothing built in this page yet (a reload, or a pasted link): back to Create.
      location.replace("#create");
      return;
    }
    mounting = true;
    el("hud").textContent = "loading…";
    let server: LocalServer | null = null;
    try {
      if (draft) {
        server = startLocalServer(draft.module, "draft", draft.spec.blurb);
        server.addBots(Math.max(0, Math.min(5, draft.module.meta.players.max - 1)));
      }
      const h = await mountGame({
        game: route.slug,
        room: route.room,
        name: nameInput.value.trim(),
        canvas: el("c") as HTMLCanvasElement,
        hud: el("hud"),
        board: el("lb"),
        buttons: Array.from(document.querySelectorAll<HTMLElement>("#touch button")),
        local: draft && server ? { module: draft.module, server } : undefined,
      });
      // The player may have hit × while the module was loading.
      if (parseRoute().view !== "game") {
        h.stop();
        server?.close();
      } else {
        handle = h;
        localServer = server;
        mounted = { slug: route.slug, room: route.room };
        if (!draft) recordRecent(route.slug);
        showControls(h.meta, draft ? draft.spec.blurb : (games[route.slug]?.blurb ?? ""), true);
      }
    } catch (err) {
      server?.close();
      el("hud").textContent = `could not start: ${(err as Error).message}`;
    } finally {
      mounting = false;
    }
  }
}

window.addEventListener("hashchange", () => void render());

// ---- controls card --------------------------------------------------------------------------
// Part of the shell, not of any game: every game starts with its name, its blurb, what A and B
// do there (meta.controls) and how to move on this device, for a few seconds. ⓘ next to ×
// brings it back; a tap on the card, or ⓘ again, dismisses it. Play goes on underneath.

const controlsEl = el("controls");
const infoButton = el("info");
let controlsTimer: number | undefined;
let controlsFor: { meta: Meta; blurb: string } | null = null;

function showControls(meta: Meta, blurb: string, auto: boolean): void {
  controlsFor = { meta, blurb };
  const coarse = matchMedia("(pointer: coarse)").matches;
  (controlsEl.querySelector("h2") as HTMLElement).textContent = meta.name;
  (controlsEl.querySelector(".what") as HTMLElement).textContent = blurb;
  (controlsEl.querySelector(".keys") as HTMLElement).textContent = meta.controls ?? "";
  (controlsEl.querySelector(".how") as HTMLElement).textContent = coarse
    ? "Drag anywhere to move. A and B are the buttons at the bottom right."
    : "Move with the mouse or WASD. A is click or space; B is right-click or the B key (or E, or shift). Hold B with WASD to act on the block next to you.";
  clearTimeout(controlsTimer);
  controlsEl.hidden = false;
  controlsEl.classList.remove("hide");
  if (auto) controlsTimer = window.setTimeout(() => hideControls(), 3500);
}

function hideControls(now = false): void {
  clearTimeout(controlsTimer);
  if (now) {
    controlsEl.hidden = true;
    controlsFor = null;
    return;
  }
  controlsEl.classList.add("hide");
  controlsTimer = window.setTimeout(() => (controlsEl.hidden = true), 600);
}

infoButton.addEventListener("click", () => {
  if (!controlsFor) return;
  const showing = !controlsEl.hidden && !controlsEl.classList.contains("hide");
  if (showing) hideControls();
  else showControls(controlsFor.meta, controlsFor.blurb, false);
});
controlsEl.addEventListener("click", () => hideControls());

// ---- feed -----------------------------------------------------------------------------------

// Previews only simulate while their card is on screen.
const visibility = new IntersectionObserver(
  (entries) => {
    for (const e of entries) {
      const card = cards.get((e.target as HTMLElement).dataset.slug ?? "");
      card?.preview?.setActive(e.isIntersecting);
    }
  },
  { threshold: 0.4 },
);

/** Cards come from the local catalogue, so the feed works even before a server exists. */
function buildCards(): void {
  for (const slug of Object.keys(games)) {
    if (cards.has(slug)) continue;
    const entry = games[slug];
    const article = document.createElement("article");
    article.className = "card";
    article.dataset.slug = slug;
    const play = `#game/${encodeURIComponent(slug)}`;
    article.innerHTML =
      `<canvas class="preview"></canvas>` +
      `<div class="panel">` +
      `<div class="head"><div class="text"><h3></h3><div class="author">@${AUTHOR}</div></div>` +
      `<a class="btn play" href="${play}">${icon("play")} Play</a>` +
      `<a class="btn remix" href="#create?remix=${encodeURIComponent(slug)}">${icon("sparkles")} Remix</a></div>` +
      `<p class="blurb">${escapeHtml(entry.blurb)}</p>` +
      `<div class="live"></div>` +
      `<div class="actions">` +
      `<button class="like" data-act="like" aria-label="Like">${icon("heart")}<span class="n">0</span></button>` +
      `<button data-act="comment" disabled aria-label="Comments">${icon("messageCircle")}<span class="n">0</span></button>` +
      `<button data-act="share" aria-label="Share">${icon("share")}<span class="n">0</span></button>` +
      `<button class="save" data-act="save" aria-label="Save">${icon("bookmark")}<span class="n">0</span></button>` +
      `<button data-act="report" aria-label="Report">${icon("flag")}</button>` +
      `</div></div>`;
    const q = (sel: string) => article.querySelector(sel) as HTMLElement;
    const card: Card = {
      el: article,
      title: q("h3"),
      live: q(".live"),
      canvas: q("canvas") as HTMLCanvasElement,
      preview: null,
      counts: {
        like: q('[data-act="like"] .n'),
        share: q('[data-act="share"] .n'),
        save: q('[data-act="save"] .n'),
      },
      buttons: {
        like: q('[data-act="like"]') as HTMLButtonElement,
        save: q('[data-act="save"]') as HTMLButtonElement,
      },
    };
    card.title.textContent = slug;
    card.buttons.like.classList.toggle("on", liked.has(slug));
    card.buttons.save.classList.toggle("on", saved.has(slug));
    for (const b of article.querySelectorAll<HTMLButtonElement>("button[data-act]")) {
      b.addEventListener("click", () => void act(slug, b.dataset.act!));
    }
    // The game itself is the biggest button on the card: a click on it (not a swipe) plays.
    card.canvas.addEventListener("click", () => {
      if (performance.now() < clickQuietUntil) return;
      location.hash = play;
    });
    cards.set(slug, card);
    feed.appendChild(article);
    visibility.observe(article);
    void mountPreview(card.canvas, slug, { labelTop: 58 }).then(
      (p) => {
        card.preview = p;
        card.title.textContent = p.name;
      },
      (err) => {
        card.live.textContent = `preview failed: ${(err as Error).message}`;
      },
    );
  }
}

function applyFilter(savedOnly: boolean): void {
  let shown = 0;
  for (const [slug, card] of cards) {
    const hide = savedOnly && !saved.has(slug);
    card.el.classList.toggle("hidden", hide);
    if (!hide) shown++;
  }
  feedEmpty.hidden = shown > 0;
  feedEmpty.innerHTML = savedOnly
    ? `Nothing saved yet.<br />Tap ${icon("bookmark")} on a game to keep it here.`
    : "No games in the catalogue.";
}

// ---- feed navigation ------------------------------------------------------------------------
// Touch swipes snap from card to card on their own. A mouse wheel does not: one tick scrolls
// about 100 px and the mandatory snap pulls the card straight back, so the feed looks stuck.
// One wheel gesture, or one arrow or page key, moves exactly one card.

function visibleCards(): HTMLElement[] {
  return Array.from(feed.children).filter((c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains("hidden"));
}

function stepFeed(dir: 1 | -1): void {
  const n = visibleCards().length;
  if (n === 0) return;
  const h = feed.clientHeight || 1;
  const next = Math.max(0, Math.min(n - 1, Math.round(feed.scrollTop / h) + dir));
  feed.scrollTo({ top: next * h, behavior: "smooth" });
}

/** A trackpad flick keeps sending wheel events while it coasts; one step per gesture. */
let wheelQuietUntil = 0;
feed.addEventListener(
  "wheel",
  (e) => {
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    e.preventDefault();
    const now = performance.now();
    if (now < wheelQuietUntil) return;
    wheelQuietUntil = now + 700;
    stepFeed(e.deltaY > 0 ? 1 : -1);
  },
  { passive: false },
);

// A mouse or pen swipe follows the pointer: the feed is locked under it while the button is
// down, then settles to the nearest card on release; a flick, or a drag past a third of the
// screen, goes to the next card. Snapping is suspended during the drag, since the mandatory
// snap would fight every pointer move. Touch is left to the browser, which already does this.
interface Drag {
  startY: number;
  startTop: number;
  lastY: number;
  lastAt: number;
  /** Pixels per millisecond, negative when swiping up. */
  velocity: number;
  /** True once the pointer has moved far enough to be a drag rather than a click. */
  active: boolean;
  pointerId: number;
}
/** A press that moves less than this is a click, and never captures the pointer. */
const DRAG_START_PX = 8;
let drag: Drag | null = null;
/** The click that ends a swipe must not count as a click on the game. */
let clickQuietUntil = 0;
const FLICK = 0.8; // px per ms
/** A pointer that rested this long before release was not flicking. */
const REST_MS = 100;

/**
 * Ease the feed to a card while snapping is off, then put snapping back. A smooth scrollTo
 * issued from a pointer handler does not start reliably, and restoring the snap while the feed
 * is between cards yanks it to whichever is nearest, so the settle is drawn by hand.
 */
function settle(toTop: number): void {
  const fromTop = feed.scrollTop;
  const t0 = performance.now();
  const duration = 260;
  let done = false;
  const finish = () => {
    if (done || drag) return; // already there, or a new drag took over
    done = true;
    feed.scrollTop = toTop;
    feed.style.scrollSnapType = "";
  };
  const step = (now: number) => {
    if (done || drag) return;
    const t = Math.min(1, (now - t0) / duration);
    const k = 1 - (1 - t) ** 3;
    feed.scrollTop = fromTop + (toTop - fromTop) * k;
    if (t < 1) requestAnimationFrame(step);
    else finish();
  };
  requestAnimationFrame(step);
  // Animation frames pause in a hidden tab; the feed must still end up on a card.
  window.setTimeout(finish, duration + 200);
}

feed.addEventListener("pointerdown", (e) => {
  if (e.pointerType === "touch" || e.button !== 0) return;
  // Buttons and links keep their clicks; a drag that starts on one would capture the pointer
  // and the click would land on the feed instead.
  if ((e.target as Element | null)?.closest("a, button, input, textarea, select")) return;
  drag = { startY: e.clientY, startTop: feed.scrollTop, lastY: e.clientY, lastAt: performance.now(), velocity: 0, active: false, pointerId: e.pointerId };
});
feed.addEventListener("dragstart", (e) => e.preventDefault()); // a link or canvas must not start a native drag
feed.addEventListener("pointermove", (e) => {
  if (!drag) return;
  if (!drag.active) {
    if (Math.abs(e.clientY - drag.startY) < DRAG_START_PX) return;
    drag.active = true;
    feed.style.scrollSnapType = "none";
    try {
      feed.setPointerCapture(drag.pointerId);
    } catch {
      /* a synthetic pointer has no capture; the drag still works while the pointer stays over the feed */
    }
  }
  const now = performance.now();
  drag.velocity = (e.clientY - drag.lastY) / Math.max(1, now - drag.lastAt);
  drag.lastY = e.clientY;
  drag.lastAt = now;
  feed.scrollTop = drag.startTop - (e.clientY - drag.startY);
});
function endDrag(e: PointerEvent): void {
  if (!drag) return;
  const d = drag;
  drag = null;
  if (!d.active) return; // a click: the canvas or whatever was pressed handles it
  const dy = e.clientY - d.startY;
  const h = feed.clientHeight || 1;
  const from = Math.round(d.startTop / h);
  const velocity = performance.now() - d.lastAt > REST_MS ? 0 : d.velocity;
  let to = from;
  if (dy < -h / 3 || velocity < -FLICK) to = from + 1;
  else if (dy > h / 3 || velocity > FLICK) to = from - 1;
  to = Math.max(0, Math.min(visibleCards().length - 1, to));
  clickQuietUntil = performance.now() + 300;
  settle(to * h);
}
feed.addEventListener("pointerup", endDrag);
feed.addEventListener("pointercancel", endDrag);

/**
 * Live games come next. Cards already scrolled past stay put, so nothing jumps under your
 * finger; every card after the one in view is reordered by players now, then catalogue order,
 * so the next swipe lands on a room with people in it whenever there is one.
 */
function orderFeed(): void {
  if (latest.length === 0) return;
  const players = new Map(latest.map((g) => [g.slug, g.players]));
  const rank = new Map(Object.keys(games).map((slug, i) => [slug, i]));
  const all = Array.from(feed.children) as HTMLElement[];
  const shown = all.filter((c) => !c.classList.contains("hidden"));
  const h = feed.clientHeight || 1;
  const current = Math.min(shown.length - 1, Math.round(feed.scrollTop / h));
  const keep = new Set(shown.slice(0, current + 1));
  const slugOf = (c: HTMLElement) => c.dataset.slug ?? "";
  const after = all.filter((c) => !keep.has(c));
  const rest = after
    .slice()
    .sort((a, b) => (players.get(slugOf(b)) ?? 0) - (players.get(slugOf(a)) ?? 0) || (rank.get(slugOf(a)) ?? 0) - (rank.get(slugOf(b)) ?? 0));
  if (rest.every((c, i) => c === after[i])) return; // already in order: leave the DOM alone
  for (const c of rest) feed.appendChild(c);
}

window.addEventListener("keydown", (e) => {
  if (parseRoute().view !== "feed") return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  const down = e.key === "ArrowDown" || e.key === "PageDown" || (e.key === " " && !e.shiftKey);
  const up = e.key === "ArrowUp" || e.key === "PageUp" || (e.key === " " && e.shiftKey);
  if (!down && !up) return;
  e.preventDefault();
  stepFeed(down ? 1 : -1);
});

function applyCounts(g: FeedGame): void {
  const card = cards.get(g.slug);
  if (!card) return;
  card.title.textContent = g.name;
  card.counts.like.textContent = fmt(g.likes);
  card.counts.share.textContent = fmt(g.shares);
  card.counts.save.textContent = fmt(g.saves);
  card.live.textContent =
    g.players === 0
      ? `${fmt(g.plays)} plays · nobody here right now`
      : `${fmt(g.plays)} plays · ${g.players} playing now${g.rooms > 1 ? ` across ${g.rooms} rooms` : ""}`;
}

async function refreshFeed(): Promise<void> {
  const address = serverAddress();
  if (!address) {
    for (const c of cards.values()) c.live.textContent = NO_SERVER_MESSAGE;
    return;
  }
  try {
    const res = await fetch(`${address.http}/api/games`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { games: FeedGame[] };
    latest = data.games;
    for (const g of latest) applyCounts(g);
    orderFeed();
  } catch (err) {
    for (const c of cards.values()) c.live.textContent = `can't reach the game server (${(err as Error).message})`;
  }
}

async function act(slug: string, action: string): Promise<void> {
  const address = serverAddress();
  const card = cards.get(slug);
  let verb = action;
  if (action === "like") {
    verb = liked.has(slug) ? "unlike" : "like";
    if (verb === "like") liked.add(slug);
    else liked.delete(slug);
    writeJSON("p2m.liked", [...liked]);
    card?.buttons.like.classList.toggle("on", liked.has(slug));
  } else if (action === "save") {
    verb = saved.has(slug) ? "unsave" : "save";
    if (verb === "save") saved.add(slug);
    else saved.delete(slug);
    writeJSON("p2m.saved", [...saved]);
    card?.buttons.save.classList.toggle("on", saved.has(slug));
    toast(verb === "save" ? "Saved" : "Removed from saved");
    if (parseRoute().view === "feed" && (parseRoute() as { saved: boolean }).saved) applyFilter(true);
  } else if (action === "share") {
    const url = `${location.origin}${location.pathname}#game/${encodeURIComponent(slug)}`;
    const name = latest.find((g) => g.slug === slug)?.name ?? slug;
    try {
      if (navigator.share) await navigator.share({ title: name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast("Link copied");
      }
    } catch {
      return; // the share sheet was dismissed
    }
  } else if (action === "report") {
    toast("Reported, thank you");
  }
  if (!address) {
    if (action === "like" || action === "save") toast("Kept on this device; no game server to tell");
    return;
  }
  try {
    const res = await fetch(`${address.http}/api/games/${encodeURIComponent(slug)}/${verb}`, { method: "POST" });
    if (res.ok) applyCounts((await res.json()) as FeedGame);
  } catch {
    /* counts refresh on the next tick anyway */
  }
}

function startFeed(): void {
  buildCards();
  for (const c of cards.values()) c.preview?.setActive(true);
  if (feedTimer !== undefined) return;
  void refreshFeed();
  feedTimer = window.setInterval(() => void refreshFeed(), FEED_REFRESH_MS);
}

function stopFeed(): void {
  for (const c of cards.values()) c.preview?.setActive(false);
  if (feedTimer === undefined) return;
  clearInterval(feedTimer);
  feedTimer = undefined;
}

// ---- popular --------------------------------------------------------------------------------

async function refreshPopular(): Promise<void> {
  const address = serverAddress();
  if (!address) {
    popularList.innerHTML = `<li><div class="rowbody muted">${NO_SERVER_MESSAGE}</div></li>`;
    return;
  }
  try {
    const res = await fetch(`${address.http}/api/games`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    latest = ((await res.json()) as { games: FeedGame[] }).games;
  } catch (err) {
    popularList.innerHTML = `<li><div class="rowbody muted">can't reach the game server (${escapeHtml((err as Error).message)})</div></li>`;
    return;
  }
  const score = (g: FeedGame) => g.likes * 3 + g.saves * 5 + g.shares * 2 + g.plays;
  const ranked = latest.slice().sort((a, b) => score(b) - score(a) || a.name.localeCompare(b.name));
  popularList.innerHTML = ranked
    .map(
      (g, i) =>
        `<li><span class="rank">${i + 1}</span><div class="rowbody"><b>${escapeHtml(g.name)}</b>` +
        `<span class="blurb">${escapeHtml(g.blurb)}</span>` +
        `<span class="stats"><span>${icon("eye")} ${fmt(g.plays)}</span><span>${icon("heart")} ${fmt(g.likes)}</span>` +
        `<span>${icon("bookmark")} ${fmt(g.saves)}</span><span>${icon("user")} ${g.players}</span></span></div>` +
        `<a class="btn-small" href="#game/${encodeURIComponent(g.slug)}">Play</a></li>`,
    )
    .join("");
}

// ---- profile --------------------------------------------------------------------------------

function recordRecent(slug: string): void {
  const now = Date.now();
  const hit = recent.find((r) => r.slug === slug);
  if (hit) {
    hit.count++;
    hit.at = now;
  } else recent.push({ slug, count: 1, at: now });
  recent.sort((a, b) => b.at - a.at);
  recent = recent.slice(0, 20);
  writeJSON("p2m.recent", recent);
}

function renderProfile(): void {
  const name = nameInput.value.trim() || "guest";
  const avatar = el("avatar");
  avatar.textContent = name.slice(0, 1).toUpperCase();
  avatar.style.background = avatarColour(name);
  // Stats of games you have made. There is no Create yet, so there are none.
  el("st-plays").textContent = "0";
  el("st-likes").textContent = "0";
  el("st-shares").textContent = "0";
  const list = el("recent");
  if (!recent.length) {
    list.innerHTML = `<div class="empty">Nothing yet. Play something from the feed.</div>`;
    return;
  }
  list.innerHTML = recent
    .map((r) => {
      const name = latest.find((g) => g.slug === r.slug)?.name ?? cards.get(r.slug)?.preview?.name ?? r.slug;
      return (
        `<div class="row"><span class="ic">${icons.gamepad}</span><div class="rowbody"><b>${escapeHtml(name)}</b>` +
        `<span>played ${r.count} time${r.count === 1 ? "" : "s"}</span></div>` +
        `<a class="btn-small" href="#game/${encodeURIComponent(r.slug)}">Play</a></div>`
      );
    })
    .join("");
}

// ---- bits -----------------------------------------------------------------------------------

let toastTimer: number | undefined;
function toast(msg: string): void {
  toastEl.textContent = msg;
  toastEl.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove("show"), 1800);
}

function fmt(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

void render();
